import React, { useState, useEffect, useCallback } from 'react';
import DatePicker from 'react-datepicker';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import { fetchAuth } from '../../utils/api';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

function TasksView({ user, handleApiError, showNotification }) {
    const calendarRef = React.useRef(null);
    const [events, setEvents] = useState([]);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [selectedDate, setSelectedDate] = useState(null);
    const [editingTask, setEditingTask] = useState(null);
    const [currentMonth, setCurrentMonth] = useState(new Date().toLocaleString('default', { month: 'long', year: 'numeric' }));

    const fetchTasks = useCallback(async () => {
        try {
            const res = await fetchAuth(`${API_URL}/api/tasks`);
            if (res.ok) {
                const tasks = await res.json();
                
                const activeTasks = tasks.filter(task => task.status !== 'Cancelled');
                
                const formattedEvents = activeTasks.map(task => {
                    const priority = task.priority?.toLowerCase() || 'medium';
                    const statusClass = task.status?.toLowerCase() === 'completed' ? 'task-status-completed' : '';
                    
                    return {
                        id: task._id,
                        title: task.title,
                        start: new Date(task.dueDate),
                        allDay: true,
                        extendedProps: task,
                        className: `task-priority-${priority} ${statusClass}`.trim()
                    };
                });
                setEvents(formattedEvents);
            }
        } catch (error) { handleApiError(error); }
    }, [handleApiError]);

    useEffect(() => {
        fetchTasks();
    }, [fetchTasks]);
    
    const handleSaveTask = async (taskData, isEditing) => {
        const url = isEditing ? `${API_URL}/api/tasks/${editingTask._id}` : `${API_URL}/api/tasks`;
        const method = isEditing ? 'PUT' : 'POST';
        try {
            const res = await fetchAuth(url, { method, body: JSON.stringify(taskData) });
            if (!res.ok) throw new Error((await res.json()).message);
            showNotification(isEditing ? 'Task updated successfully!' : 'Task added successfully!', 'success');
            setIsAddModalOpen(false);
            setIsEditModalOpen(false);
            fetchTasks();
        } catch (err) { handleApiError(err); }
    };
    
    const handleDateClick = (clickInfo) => {
        setSelectedDate(clickInfo.date);
        setIsAddModalOpen(true);
    };

    const handleEventClick = (clickInfo) => {
        setEditingTask(clickInfo.event.extendedProps);
        setIsEditModalOpen(true);
    };
    
    const updateHeaderDate = () => {
        if (calendarRef.current) {
            const calendarApi = calendarRef.current.getApi();
            setCurrentMonth(calendarApi.view.title);
        }
    };

    const TaskModal = ({ onClose, onSave, isEdit, initialData }) => {
        const [task, setTask] = useState(initialData);
        const handleChange = (e) => setTask(prev => ({...prev, [e.target.name]: e.target.value}));
        return (
            <div className="modal-overlay">
                <div className="modal-content">
                    <h3>{isEdit ? 'Update Task' : 'Add New Task'}</h3>
                    <form onSubmit={(e) => { e.preventDefault(); onSave(task, isEdit); }}>
                        <div className="input-group"><label>Task Title *</label><input name="title" value={task.title} onChange={handleChange} required /></div>
                        <div className="form-grid-2">
                            <div className="input-group"><label>Due Date</label><DatePicker selected={new Date(task.dueDate)} onChange={(date) => setTask(p => ({...p, dueDate: date}))} className="customer-input"/></div>
                            <div className="input-group"><label>Priority</label><select name="priority" value={task.priority} onChange={handleChange}><option>Medium</option><option>High</option><option>Low</option></select></div>
                        </div>
                        <div className="input-group">
                            <label>Status</label>
                            <select name="status" value={task.status} onChange={handleChange}>
                                <option>Not Started</option>
                                <option>In Progress</option>
                                <option>Completed</option>
                                <option>Cancelled</option>
                            </select>
                        </div>                        <div className="input-group"><label>Description</label><textarea name="description" value={task.description} onChange={handleChange} rows="3"></textarea></div>
                        <div className="modal-actions"><button type="button" className="cancel-btn" onClick={onClose}>Cancel</button><button type="submit" className="confirm-btn">{isEdit ? 'Update Task' : 'Create Task'}</button></div>
                    </form>
                </div>
            </div>
        );
    };

     return (
        <div className="page-container">
            {isAddModalOpen && <TaskModal onClose={() => setIsAddModalOpen(false)} onSave={handleSaveTask} isEdit={false} initialData={{ title: '', dueDate: selectedDate || new Date(), priority: 'Medium', status: 'Not Started', description: '' }} />}
            {isEditModalOpen && <TaskModal onClose={() => setIsEditModalOpen(false)} onSave={handleSaveTask} isEdit={true} initialData={editingTask} />}
            
            <div className="page-header">
                 <h2 className="page-title">Task Calendar</h2>
                <div className="calendar-nav">
                    <button onClick={() => calendarRef.current?.getApi().today()}>Today</button>
                    <button onClick={() => calendarRef.current?.getApi().prev()}>&lt;</button>
                    <button onClick={() => calendarRef.current?.getApi().next()}>&gt;</button>
                    <span className="calendar-title">{currentMonth}</span>
                </div>
                <div className="header-actions">
                    <button className="btn-secondary">History</button>
                    <button className="btn-primary" onClick={() => handleDateClick({ date: new Date() })}>+ Add Task</button>
                </div>
            </div>
            <div className="calendar-container">
                <FullCalendar
                    ref={calendarRef}
                    plugins={[dayGridPlugin, interactionPlugin]}
                    initialView="dayGridMonth"
                    headerToolbar={false}
                    events={events}
                    datesSet={updateHeaderDate}
                    dateClick={handleDateClick}
                    eventClick={handleEventClick}
                    aspectRatio={1.2}
                />
            </div>
        </div>
    );
}

export default TasksView;
